import React from "react";
import { Grid } from "@material-ui/core";
import { ApolloClient, InMemoryCache, HttpLink, gql } from "@apollo/client";
import { formatBookingJson, manualGoogleEvents, trackProCardsViewsOnViewport } from "../helpers/helpers";
import CallToActionDescription from "./call-to-action-description";
import { ProfileServiceCard, ServiceCard } from "@hometown/react-hometown-lib";

const BASE_SERVICE_ID = "62a9f2cd61706a135071a7e3";

// Extract yard size from a service title (e.g. "4 Yard Dumpster Rental" → 4).
// Used as fallback when htServices description doesn't exactly match pppPrices title.
const sizeFromTitle = (title) => {
    const m = title?.match(/(\d+)\s*yard/i);
    return m ? parseInt(m[1]) : 9999;
};

const HT_SERVICES_QUERY = gql`
  query HtServices($idBaseService: String!, $wasteType: [Int], $dumpsterType: Int, $dumpsterSize: String, $proId: Int, $placesId: [Int], $skip: Int, $limit: Int, $event: event, $datePeriod: Int, $includeNullPrices: Boolean, $pros: [Int]) {
    htServices(idBaseService: $idBaseService, wasteType: $wasteType, dumpsterType: $dumpsterType, dumpsterSize: $dumpsterSize, proId: $proId, placesId: $placesId, skip: $skip, limit: $limit, event: $event, datePeriod: $datePeriod, includeNullPrices: $includeNullPrices, pros: $pros) {
      nodes {
        _id
        price
        proId
        dumpsterSize
        description
        placesId { placeId distanceFee }
      }
      popularPros
      sponsoredPros {
        _id
        price
        proId
        dumpsterSize
        description
        placesId { placeId distanceFee }
      }
      pageInfo {
        currentPage
        hasNextPage
        perPage
        totalCount
      }
    }
  }
`;

export default class ProfilesCardsDumpster extends React.Component {



    constructor(props) {
        super(props)

        this.profiles = this.props.profiles.length > 0 ? this.props.profiles : [];
        this.profilesById = this.props.cards
        this.cardsPerPage = 6;
        this.defaultCards = 15;
        // Hub01 to Hub09 limit to 10 cards per page
        if(this.props.place.ht?.includes('Hub')){
			this.defaultCards = 10;
		}
        this.page = 0;

        this.code = this.props.code;
        this.place = this.props.place;
        this.onlineBookingPros = this.props.online_booking_pros;
        this.commercialProjectType = "509"
        this.paginated = this.props.paginated
        this.eventClass = this.props.eventClass || "proCardRendered"
        this.cardClass = this.props.cardClass || "card-pro-view"
        this.pppPrices = this.props.pppPrices || {}
        this.useServiceCards = this.props.useServiceCards || false
        this.mode = this.props.mode || 'default'
        this.state = {
            placeId: null,
            realPrices: {},
            isLoading: Object.keys(this.props.pppPrices || {}).length > 0,
        }
    }

    componentDidMount() {
        trackProCardsViewsOnViewport(this.eventClass, this.cardClass, 0);
        if (Object.keys(this.pppPrices).length > 0) {
            this.loadPlaceIdAndRealPrices();
        }
        window.addEventListener('pageshow', this.handlePageShow);
    }

    componentWillUnmount() {
        window.removeEventListener('pageshow', this.handlePageShow);
    }

    handlePageShow = (event) => {
        if (event.persisted && Object.keys(this.pppPrices).length > 0) {
            this.setState({ realPrices: {}, placeId: null }, () => {
                this.loadPlaceIdAndRealPrices();
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.overridePlaceId && this.props.overridePlaceId !== prevProps.overridePlaceId) {
            this.loadPlaceIdAndRealPrices(this.props.overridePlaceId);
        }
    }

    loadPlaceIdAndRealPrices = async (forcedPlaceId = null) => {
        if (typeof window === 'undefined') return;

        if (forcedPlaceId) {
            this.setState({ isLoading: true });
        }

        try {
            let placeId = forcedPlaceId;

            if (!placeId) {
                // Read placeId + wasteType from localStorage (set by ServiceSeeker / view-prices form)
                let storedWaste = null;
                let storedFullFormData = null;
                try {
                    const stored = localStorage.getItem('onlineBookingData');
                    if (stored) {
                        const data = JSON.parse(stored);
                        if (data.placeId) placeId = +data.placeId;
                        const w = data.initialValues?.find(e => e.key === 'wasteType')?.value
                               || data.finalDataForBooking?.find(e => e.key === 'wasteType')?.value;
                        storedWaste = Array.isArray(w) ? +w[0] : (w != null ? +w : null);
                        storedFullFormData = data.initialValues?.find(e => e.key === 'fullFormData')?.value
                                         || data.finalDataForBooking?.find(e => e.key === 'fullFormData')?.value || null;
                    }
                } catch (e) {}

                // Last resort: resolve Hometown placeId from zip+city stored in fullFormData.
                // Handles the case where formatBookingJson was called with placeId=null
                // (e.g. form submitted on a KPP before the address resolution happened).
                if (!placeId && storedFullFormData?.odf_postal_code && storedFullFormData?.odf_locality) {
                    try {
                        const zip = storedFullFormData.odf_postal_code;
                        const city = storedFullFormData.odf_locality.replace(/ /g, '-');
                        const res = await fetch(
                            `${process.env.GATSBY_DRUPAL_API_MANAGMENT}/place-by-zip-and-name/${zip}/${city}`,
                            { headers: { 'Ocp-Apim-Subscription-Key': '30ca1663de42420bb4a9894d1898e91e' } }
                        );
                        const resolved = await res.json();
                        if (resolved?.id) placeId = +resolved.id;
                    } catch (e) {}
                }

                if (!placeId) {
                    console.log('[PPP] No placeId found in localStorage or URL — hasRealPrice will be false for all services');
                    return;
                }

                // Only load prices when the stored selection matches THIS panel page's
                // place + waste type. A different city/waste is a different search — skip.
                if (this.props.pagePlaceId && +placeId !== +this.props.pagePlaceId) {
                    console.log('[PPP] Stored place differs from page place — skipping price load', placeId, this.props.pagePlaceId);
                    return;
                }
                if (this.props.pageWasteType && storedWaste != null && storedWaste !== +this.props.pageWasteType) {
                    console.log('[PPP] Stored waste differs from page waste — skipping price load', storedWaste, this.props.pageWasteType);
                    return;
                }
            }
            console.log('[PPP] placeId resolved:', placeId);

            const proIds = Object.keys(this.pppPrices);
            if (!proIds.length) return;

            const realPrices = {};
            const headers = { 'Ocp-Apim-Subscription-Key': '30ca1663de42420bb4a9894d1898e91e' };
            const client = new ApolloClient({
                link: new HttpLink({ uri: process.env.GATSBY_BOOKABLE_SEARCH_API, headers }),
                cache: new InMemoryCache(),
                defaultOptions: { query: { fetchPolicy: 'no-cache', errorPolicy: 'all' } },
            });

            const result = await client.query({
                query: HT_SERVICES_QUERY,
                variables: {
                    idBaseService: BASE_SERVICE_ID,
                    wasteType: [this.props.pageWasteType || 1],
                    placesId: [placeId],
                    skip: 0,
                    limit: 100,
                },
            });

            const nodes = result.data?.htServices?.nodes || [];

            nodes.forEach(s => {
                if (!s.proId) return;
                const proId = String(s.proId);
                if (!this.pppPrices[proId]) return;
                const placeData = s.placesId?.find(p => +p.placeId === placeId);
                const distanceFee = placeData?.distanceFee ? parseFloat(placeData.distanceFee) : 0;
                if (!realPrices[proId]) realPrices[proId] = [];
                realPrices[proId].push({
                    id: s._id,
                    title: s.description,
                    price: s.price ? (parseFloat(s.price) + distanceFee).toFixed(2) : null,
                    dumpsterSize: s.dumpsterSize,
                });
            });

            console.log('[PPP] Real prices loaded:', realPrices);
            const hasRealPrices = Object.values(realPrices).some(arr => arr.length > 0)
            this.setState({ placeId, realPrices }, () => {
                this.props.onLoaded?.(hasRealPrices)
            });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    addQuote = (id) => {
        if(this.onlineBookingPros.includes(+id) && this.code !== 'comercial-trash-service' && this.code !== 'junk-removal'){
            return(
                <CallToActionDescription 
                    detail=""
                    startVisible={false}
                    proid={id}
                    class="get-a-quote view-prices"
                    ondeffCode='viewPricesForm'
                    btnLabel='View prices & book'
                    callBackAfterSubmit={this.handleCallBackAfterSubmit}
                    onlineBooking={true}
                    proUrl={this.getProUrl(this.profilesById[id])}
                />
            )
        } else {
            if(this.code === 'comercial-trash-service'){
                return (
                    <CallToActionDescription 
                        detail="Request pricing & availability"
                        startVisible={false}
                        proid={id}
                        initialFormData={[{ key: "pro_id", value: id }, { key: "project_type", value: this.commercialProjectType }]}
                        ondeffCode='commercialWasteQuoteRequest'
                        class="get-a-quote"
                    />
                )
            }
            else if(this.code === 'junk-removal'){
                return (
                    <CallToActionDescription 
                        detail="Request pricing & availability"
                        startVisible={false}
                        proid={id}
                        ondeffCode='junkRemovalQuoteRequest'
                        class="get-a-quote"
                    />
                ) 
            } else{
                return (
                    <CallToActionDescription 
                        detail=""
                        startVisible={false}
                        proid={id}
                        ondeffCode='priceAndAvailabilityForm'
                        class="get-a-quote view-prices-and-availability"
                        btnLabel='Get pricing & availability'
                    />
                )  
            }
        }
    }

    extractProInfo = (html) => {
        if (!html) return {}
        const name = (html.match(/<[^>]+data-id="name"[^>]*>([^<]+)<\/[^>]+>/) || [])[1]?.trim() || ''
        const rating = (html.match(/<[^>]+data-id="ratingValue"[^>]*>([^<]+)<\/[^>]+>/) || [])[1]?.trim() || ''
        const reviewCount = (html.match(/<[^>]+data-id="reviewCount"[^>]*>([^<]+)<\/[^>]+>/) || [])[1]?.trim() || ''
        return { name, rating, reviewCount }
    }

    getProUrl = (data) => {
        const url = data?.split(/data-url="([^"]+)/)
        return url?.length === 3 ? url[1] : '/book';
    }

    handleRedirect = (data) => {
        const proUrl = this.getProUrl(data);
        if(proUrl){
            window.location.href = proUrl;
        }
    }

    handleCallBackAfterSubmit = (formData) => {
        const formattedData = formatBookingJson(formData);
        if(formattedData?.pro_id && this.profilesById[formattedData.pro_id]) {
            manualGoogleEvents('manualFormSubmition');
            this.handleRedirect(this.profilesById[formattedData.pro_id])
        }
    }

    renderAllServices() {
        const allServices = [];
        const seenProIds = new Set();
        this.profiles.forEach(profile => {
            if (seenProIds.has(profile.id)) return;
            seenProIds.add(profile.id);
            const proHtml = this.profilesById[profile.id];
            if (!proHtml) return;
            const proPrices = this.pppPrices[profile.id] || [];
            if (!proPrices.length) return;
            const proUrl = this.getProUrl(proHtml);
            const proInfo = this.extractProInfo(proHtml);
            const profileData = { n: proInfo.name, r: +proInfo.reviewCount || 0, rt: proInfo.rating || null, proId: profile.id };
            proPrices.forEach(item => allServices.push({ ...item, proId: profile.id, proUrl, profileData }));
        });

        allServices.sort((a, b) => sizeFromTitle(a.title) - sizeFromTitle(b.title));

        const realPrices = this.state.realPrices;
        return (
            <Grid container direction="row" alignItems="stretch">
                    {allServices.map((item, i) => {
                        const realProPrices = realPrices[item.proId];
                        const realService = realProPrices?.find(r => r.title?.toLowerCase() === item.title?.toLowerCase() || (sizeFromTitle(item.title) !== 9999 && r.dumpsterSize != null && +r.dumpsterSize === sizeFromTitle(item.title)));
                        const hasRealPrice = !!realService;
                        const serviceToShow = realService ? { ...item, id: realService.id, price: realService.price } : item;
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={i} data-proid={item.proId} className="gridContainer">
                                <ServiceCard
                                    service={serviceToShow}
                                    placeId={hasRealPrice ? this.state.placeId : null}
                                    profileData={item.profileData}
                                    basePricesMap={{ [item.id]: item.price }}
                                    imageStaticEnv={`${process.env.GATSBY_SITE_DOMAIN}/assets/`}
                                    numberOfSameProServices={this.pppPrices[item.proId]?.length || 1}
                                    isLoadingServices={this.state.isLoading}
                                    handleServiceClick={() => {
                                        if (this.props.onCardDetailsClick) {
                                            const sameProServices = allServices.filter(s => s.proId === item.proId);
                                            if (!hasRealPrice) {
                                                this.props.onCardDetailsClick({ service: item, hasRealPrice, placeId: null, profileData: item.profileData, proUrl: item.proUrl, proServices: sameProServices });
                                                return;
                                            }
                                            const rp2 = this.state.realPrices[item.proId];
                                            let available2 = rp2 ? [...rp2] : [];
                                            const matchedServices2 = sameProServices.map(s => {
                                                const matchIdx = available2.findIndex(r => r.title?.toLowerCase() === s.title?.toLowerCase() || (sizeFromTitle(s.title) !== 9999 && r.dumpsterSize != null && +r.dumpsterSize === sizeFromTitle(s.title)));
                                                if (matchIdx >= 0) {
                                                    const rs = available2[matchIdx];
                                                    available2.splice(matchIdx, 1);
                                                    return { ...s, id: rs.id, price: rs.price };
                                                }
                                                return null;
                                            });
                                            const clickedIndex = sameProServices.indexOf(item);
                                            const serviceForDetails = matchedServices2[clickedIndex];
                                            if (!serviceForDetails) {
                                                item.proUrl && (window.location.href = item.proUrl);
                                                return;
                                            }
                                            const proAllServices = matchedServices2.filter(Boolean);
                                            this.props.onCardDetailsClick({ service: serviceForDetails, hasRealPrice, placeId: this.state.placeId, profileData: item.profileData, proUrl: item.proUrl, proServices: proAllServices });
                                        } else if (item.proUrl) {
                                            window.location.href = item.proUrl;
                                        }
                                    }}
                                    callbackFunction={(data) => { this.props.bookingCallback ? this.props.bookingCallback(data) : (item.proUrl && (window.location.href = item.proUrl)) }}
                                    finalData={[{ key: 'proUrl', value: item.proUrl }, { key: 'placeId', value: this.state.placeId }]}
                                    onViewPricing={() => { this.props.ondeffCallback ? this.props.ondeffCallback(item.proId) : (item.proUrl && (window.location.href = item.proUrl)) }}
                                />
                            </Grid>
                        );
                    })}
                    {this.props.appendCard && (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={3} className="gridContainer">
                            {this.props.appendCard}
                        </Grid>
                    )}
            </Grid>
        );
    }

    render() {
        if (this.mode === 'all-services') {
            return this.renderAllServices();
        }

        const handleClick = async (e) => {
            e.preventDefault();
            var profilesByIdAux = this.profilesById
            const localCode = this.code === 'dumpster' ? 'dumpster-rental' : this.code === 'comercial-trash-service' ? 'commercial-waste-management' : this.code
            this.page = this.page + 1;
            const previousCount = this.defaultCards + (this.page - 1) * this.cardsPerPage;
            await Promise.all(this.profiles.slice(this.defaultCards + ((this.page - 1)*this.cardsPerPage), (this.defaultCards + this.page*this.cardsPerPage)).map((profile, index) => {
                return fetch('/pros/' + localCode + '/' + profile.id + '.html')
                .then(res => {
                    return res.text();
                })
                .then(res => {
                    if (res.startsWith("<div class=\"flex-head\"")) {
                        res = res.replace(/\[place\]/g, this.place.name);
                        profilesByIdAux[profile.id] = res;
                        return profile
                    }
                    else {
                        throw new Error('Invalid card');
                    }
                    
                }).catch((error) => {
                    profile.remove = 1
                    return profile
                });
            })).then(() => {
                this.setState({
                    profilesById: profilesByIdAux
                })
                return this.forceUpdate()
            })
            trackProCardsViewsOnViewport(this.eventClass, this.cardClass, previousCount);
            return         
        }
        return (
            <div className="items-grid prof dumpster">
                <div className="container">
                    <div id="profiles-cards-list" className="row">
                        {
                            this.profiles.slice(0, (this.paginated ? (this.defaultCards + this.page*this.cardsPerPage) : this.profiles.length)).filter(item => item.remove != 1).map((profile, index) => {
                               const pro = profile
                               if(this.profilesById[pro.id]){
                                    const proHtml = this.profilesById[pro.id]
                                    const proPrices = this.pppPrices[pro.id] || []
                                    const proUrl = this.getProUrl(proHtml)

                                    // Section 3 PPP: render ServiceCard (vertical) per service, no HTML blob
                                    if (this.useServiceCards && proPrices.length > 0) {
                                        const proInfo = this.extractProInfo(proHtml)
                                        const profileData = {
                                            n: proInfo.name,
                                            r: proInfo.reviewCount ? +proInfo.reviewCount : 0,
                                            rt: proInfo.rating || null,
                                            proId: pro.id,
                                        }
                                        return proPrices.map((item, i) => {
                                            const realProPrices = this.state.realPrices[pro.id]
                                            const realService = realProPrices?.find(r => r.title?.toLowerCase() === item.title?.toLowerCase() || (sizeFromTitle(item.title) !== 9999 && r.dumpsterSize != null && +r.dumpsterSize === sizeFromTitle(item.title)))
                                            const hasRealPrice = !!realService
                                            const serviceToShow = realService ? { ...item, id: realService.id, price: realService.price } : item
                                            const basePricesMap = { [item.id]: item.price }
                                            return (
                                                <div data-proid={pro.id} key={`${index}-${i}`} className={this.cardClass}>
                                                    <ServiceCard
                                                        service={serviceToShow}
                                                        placeId={hasRealPrice ? this.state.placeId : null}
                                                        profileData={profileData}
                                                        basePricesMap={basePricesMap}
                                                        imageStaticEnv={`${process.env.GATSBY_SITE_DOMAIN}/assets/`}
                                                        numberOfSameProServices={proPrices.length}
                                                        handleServiceClick={() => { if (proUrl) window.location.href = proUrl }}
                                                        callbackFunction={(data) => { this.props.bookingCallback ? this.props.bookingCallback(data) : (proUrl && (window.location.href = proUrl)) }}
                                                        finalData={[{ key: 'proUrl', value: proUrl }, { key: 'placeId', value: this.state.placeId }]}
                                                        onViewPricing={() => { if (proUrl) window.location.href = proUrl }}
                                                    />
                                                </div>
                                            )
                                        })
                                    }

                                    // Default: HTML blob + ProfileServiceCard horizontal
                                    return (
                                        <div data-proid={pro.id} key={index} className={this.cardClass}>
                                            <div data-id={pro.id} className={`item${proPrices.length > 0 ? ' has-price-cards' : ''}`}>
                                                <div dangerouslySetInnerHTML={{__html: proHtml}} onClick={() => this.handleRedirect(proHtml)} />
                                                {proPrices.length > 0 && (
                                                    <div className="ppp-service-cards">
                                                        {proPrices.map((item, i) => {
                                                            const realProPrices = this.state.realPrices[pro.id]
                                                            const realService = realProPrices?.find(r => r.title?.toLowerCase() === item.title?.toLowerCase() || (sizeFromTitle(item.title) !== 9999 && r.dumpsterSize != null && +r.dumpsterSize === sizeFromTitle(item.title)))
                                                            const hasRealPrice = !!realService
                                                            const serviceToShow = realService ? { ...item, id: realService.id, price: realService.price } : item
                                                            const proInfo = this.extractProInfo(proHtml)
                                                            const profileData = { n: proInfo.name, r: proInfo.reviewCount ? +proInfo.reviewCount : 0, rt: proInfo.rating || null, proId: pro.id }
                                                            return (
                                                                <ProfileServiceCard
                                                                    key={i}
                                                                    service={serviceToShow}
                                                                    hasOnlineBooking={this.onlineBookingPros?.includes(+pro.id)}
                                                                    proUrl={proUrl}
                                                                    proId={pro.id}
                                                                    imageStaticEnv={`${process.env.GATSBY_SITE_DOMAIN}/assets/`}
                                                                    hasRealPrice={hasRealPrice}
                                                                    profileData={profileData}
                                                                    onCardDetailsClick={this.props.onCardDetailsClick ? () => {
                                                                        if (!hasRealPrice) {
                                                                            this.props.onCardDetailsClick({ service: serviceToShow, hasRealPrice, placeId: null, profileData, proUrl, proServices: proPrices });
                                                                            return;
                                                                        }
                                                                        const rp = this.state.realPrices[pro.id];
                                                                        let available = rp ? [...rp] : [];
                                                                        const matchedServices = proPrices.map(s => {
                                                                            const matchIdx = available.findIndex(r => r.title?.toLowerCase() === s.title?.toLowerCase() || (sizeFromTitle(s.title) !== 9999 && r.dumpsterSize != null && +r.dumpsterSize === sizeFromTitle(s.title)));
                                                                            if (matchIdx >= 0) {
                                                                                const rs = available[matchIdx];
                                                                                available.splice(matchIdx, 1);
                                                                                return { ...s, id: rs.id, price: rs.price };
                                                                            }
                                                                            return null;
                                                                        });
                                                                        const itemIndex = proPrices.indexOf(item);
                                                                        const serviceForDetails = matchedServices[itemIndex];
                                                                        if (!serviceForDetails) {
                                                                            this.props.ondeffCallback ? this.props.ondeffCallback(pro.id) : (proUrl && (window.location.href = proUrl));
                                                                            return;
                                                                        }
                                                                        const proAllServices = matchedServices.filter(Boolean);
                                                                        console.log('[PPP] hasRealPrice=true → opening card details', { proAllServices });
                                                                        this.props.onCardDetailsClick({ service: serviceForDetails, hasRealPrice, placeId: this.state.placeId, profileData, proUrl, proServices: proAllServices });
                                                                    } : undefined}
                                                                    isLoading={this.state.isLoading}
                                                                    callbackFunction={(data) => { this.props.bookingCallback ? this.props.bookingCallback([...data, { key: 'proUrl', value: proUrl }, { key: 'placeId', value: this.state.placeId }]) : (proUrl && (window.location.href = proUrl)) }}
                                                                    ondeffCallback={() => {
                                                                        this.props.ondeffCallback ? this.props.ondeffCallback(pro.id) : (proUrl && (window.location.href = proUrl))
                                                                    }}
                                                                />
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                                {!proPrices.length && this.addQuote(pro.id)}
                                            </div>
                                        </div>
                                    )
                                }else{
                                    profile.remove = 1
                                    return <></>
                                }
                            })
                        }
                    </div>
                    {this.paginated && this.profiles?.length > (this.defaultCards + this.page*this.cardsPerPage) &&
                    <div className="load-more-container">
                        <button onClick={handleClick} className="load-more-btn">Show More</button>
                    </div>
                   }
                </div>
            </div>
        )
    }
}